<template>
  <div class="flex flex-col h-full w-full">
    <div class="flex flex-col h-full bg-white p-5 rounded-xl overflow-auto">
      <!-- Back link -->
      <button
        type="button"
        class="self-start inline-flex items-center gap-1 text-sm text-gray-600 hover:text-primary-900 cursor-pointer mb-3"
        @click="goBack"
      >
        <v-icon size="18">mdi-arrow-left</v-icon>
        {{ $t("reports.call_analysis.back") }}
      </button>

      <!-- Header -->
      <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div class="flex flex-col gap-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-xs font-mono text-gray-500 tracking-wide">
              {{ $t("reports.call_analysis.head_call_id") }}: {{ callId }}
            </span>
            <span
              v-if="analysisStatus"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
              :class="statusBadgeClass"
            >
              {{ analysisStatus }}
            </span>
          </div>
          <h2 class="text-xl font-semibold text-gray-800">
            {{ $t("reports.call_analysis.title") }}
          </h2>
          <div class="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600 mt-1">
            <span v-if="ctx.number">
              <span class="text-gray-400">{{ $t("reports.call_analysis.head_number") }}:</span>
              {{ ctx.number }}
            </span>
            <span v-if="ctx.agent">
              <span class="text-gray-400">{{ $t("reports.call_analysis.head_agent") }}:</span>
              {{ ctx.agent }}
            </span>
            <span v-if="ctx.date">
              <span class="text-gray-400">{{ $t("reports.call_analysis.head_date") }}:</span>
              {{ ctx.date }}
            </span>
          </div>
        </div>

        <!-- Recording -->
        <div v-if="ctx.recordingfile" class="flex items-center gap-2">
          <CustomAudioPlayer
            v-if="recordingUrl"
            :src="recordingUrl"
            :autoplay="false"
            :loop="false"
          />
          <button
            v-else
            type="button"
            class="inline-flex items-center gap-1.5 bg-primary-900 hover:bg-primary-500 text-white text-sm rounded px-3 py-1.5 cursor-pointer disabled:opacity-60"
            :disabled="recordingLoading"
            @click="loadRecording"
          >
            <v-progress-circular
              v-if="recordingLoading"
              class="!h-4 !w-4"
              width="2"
              indeterminate
              color="white"
            />
            <v-icon v-else size="small">mdi-headphones</v-icon>
            {{ $t("reports.call_analysis.load_recording") }}
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-1 justify-center items-center py-16">
        <v-progress-circular indeterminate color="primary" />
        <span class="ml-3 text-gray-500">{{ $t("reports.call_analysis.loading") }}</span>
      </div>

      <!-- Error / not found -->
      <div
        v-else-if="error || !hasAnalysis"
        class="flex flex-col flex-1 justify-center items-center py-16 text-center"
      >
        <v-icon :color="error ? 'error' : 'grey'" size="48">
          {{ error ? "mdi-alert-circle-outline" : "mdi-text-box-remove-outline" }}
        </v-icon>
        <p class="mt-3 text-gray-500">
          {{ error || $t("reports.call_analysis.not_found") }}
        </p>
      </div>

      <!-- Content -->
      <div v-else class="flex flex-col gap-5">
        <!-- Metric cards -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- QA score -->
          <div
            v-if="qa"
            class="bg-white rounded-xl border border-gray-200 p-4 flex flex-col"
          >
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {{ $t("reports.call_analysis.qa_score") }}
            </span>
            <div class="flex items-end gap-2 mt-1">
              <span class="text-3xl font-bold" :class="qaScoreColor">
                {{ qa.score != null ? qa.score : "-" }}
              </span>
              <span v-if="qa.score != null" class="text-gray-400 mb-1 text-sm">/ 100</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                class="h-2 rounded-full transition-all duration-300"
                :class="qaScoreBarColor"
                :style="{ width: (qa.score || 0) + '%' }"
              ></div>
            </div>
            <div class="flex items-center justify-between mt-3 text-xs">
              <span class="text-gray-600">
                {{ $t("reports.call_analysis.qa_checks_passed") }}:
                <span class="font-semibold">{{ qa.passed_checks ?? "-" }}/{{ qa.total_checks ?? "-" }}</span>
              </span>
              <span
                class="inline-flex items-center gap-1 font-medium"
                :class="qa.has_required_failures ? 'text-red-600' : 'text-green-600'"
              >
                <v-icon size="14">
                  {{ qa.has_required_failures ? "mdi-alert" : "mdi-check-circle" }}
                </v-icon>
                {{
                  qa.has_required_failures
                    ? $t("reports.call_analysis.qa_required_failure")
                    : $t("reports.call_analysis.qa_no_failures")
                }}
              </span>
            </div>
          </div>

          <!-- Customer sentiment -->
          <div
            v-if="sentiment"
            class="bg-white rounded-xl border border-gray-200 p-4 flex flex-col"
          >
            <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {{ $t("reports.call_analysis.customer_sentiment") }}
            </span>
            <div class="flex items-center gap-2 mt-1">
              <span
                class="px-2.5 py-1 rounded-full text-sm font-semibold"
                :class="sentimentChipClass(sentiment.customer_score)"
              >
                {{ sentimentLabel(sentiment.customer_score) }}
              </span>
              <span class="text-gray-500 text-sm">
                {{ $t("reports.call_analysis.score") }}: {{ formatSigned(sentiment.customer_score) }}
              </span>
            </div>
            <div class="mt-3">
              <div class="flex justify-between text-[11px] text-gray-400 mb-1">
                <span>{{ $t("reports.call_analysis.negative") }}</span>
                <span>{{ $t("reports.call_analysis.neutral") }}</span>
                <span>{{ $t("reports.call_analysis.positive") }}</span>
              </div>
              <div class="relative w-full bg-gradient-to-r from-red-200 via-amber-100 to-green-200 rounded-full h-2">
                <div
                  class="absolute -top-1 w-1.5 h-4 rounded bg-gray-800"
                  :style="{ left: scoreToPercent(sentiment.customer_score) + '%' }"
                ></div>
              </div>
            </div>
          </div>

        </div>

        <!-- Summary (shown when present) -->
        <div v-if="summaryHtml" class="bg-stone-50 rounded-xl border border-stone-200 p-4">
          <h3 class="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <v-icon size="18" color="primary">mdi-text-box-outline</v-icon>
            {{ $t("reports.call_analysis.summary_section_title") }}
          </h3>
          <div
            class="prose prose-sm max-w-none text-gray-800 leading-relaxed"
            :dir="containsArabic(summaryText) ? 'rtl' : 'ltr'"
            v-html="summaryHtml"
          ></div>
        </div>

        <!-- QA checks -->
        <div v-if="qaChecks.length" class="bg-white rounded-xl border border-gray-200 p-4">
          <h3 class="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <v-icon size="18" color="primary">mdi-clipboard-check-outline</v-icon>
            {{ $t("reports.call_analysis.qa_section_title") }}
          </h3>
          <div class="space-y-2">
            <div
              v-for="(check, idx) in qaChecks"
              :key="idx"
              class="rounded-lg border p-3"
              :class="check.passed ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'"
            >
              <div class="flex items-start gap-2">
                <v-icon
                  :color="check.passed ? 'green-darken-1' : 'red-darken-1'"
                  size="20"
                  class="mt-0.5 flex-shrink-0"
                >
                  {{ check.passed ? "mdi-check-circle" : "mdi-close-circle" }}
                </v-icon>
                <div class="flex-1 min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="font-medium text-gray-800">{{ check.check_name }}</span>
                    <span
                      v-if="check.check_type"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase tracking-wide"
                    >
                      {{ formatCheckType(check.check_type) }}
                    </span>
                    <span
                      class="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide"
                      :class="check.is_required ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'"
                    >
                      {{ check.is_required ? $t("reports.call_analysis.required") : $t("reports.call_analysis.optional") }}
                    </span>
                    <span
                      v-if="check.confidence != null"
                      class="text-[11px] text-gray-400 ml-auto"
                    >
                      {{ $t("reports.call_analysis.confidence") }}: {{ formatPercent(check.confidence) }}
                    </span>
                  </div>
                  <p
                    v-if="check.reasoning"
                    class="text-sm text-gray-600 mt-1"
                    :dir="containsArabic(check.reasoning) ? 'rtl' : 'ltr'"
                  >
                    {{ check.reasoning }}
                  </p>
                  <p
                    v-if="check.evidence"
                    class="text-xs text-gray-500 mt-1 italic border-l-2 border-gray-200 pl-2"
                    :dir="containsArabic(check.evidence) ? 'rtl' : 'ltr'"
                  >
                    "{{ check.evidence }}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Transcript -->
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <h3 class="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <v-icon size="18" color="primary">mdi-message-text-outline</v-icon>
            {{ $t("reports.call_analysis.transcript_section_title") }}
          </h3>
          <div v-if="transcriptLines.length === 0" class="text-center py-6 text-gray-400 text-sm">
            {{ $t("reports.call_analysis.no_transcript") }}
          </div>
          <div v-else class="space-y-2 text-sm leading-relaxed">
            <div
              v-for="(line, idx) in transcriptLines"
              :key="idx"
              class="rounded-md px-3 py-2 border whitespace-pre-wrap break-words"
              :class="line.speaker === 'agent'
                ? 'border-blue-100 bg-blue-50/50'
                : 'border-green-100 bg-green-50/50'"
              :dir="containsArabic(line.text) ? 'rtl' : 'ltr'"
            >
              <span
                class="font-semibold"
                :class="line.speaker === 'agent' ? 'text-blue-700' : 'text-green-700'"
              >
                {{ line.speaker === "agent" ? $t("reports.call_analysis.role_agent") : $t("reports.call_analysis.role_customer") }}:
              </span>
              {{ line.text }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { marked } from "marked";
import CustomAudioPlayer from "@/components/CustomAudioPlayer.vue";

export default {
  name: "call-analysis-details",
  components: { CustomAudioPlayer },
  data() {
    return {
      loading: false,
      error: null,
      analysis: null,
      recordingUrl: null,
      recordingLoading: false,
    };
  },
  computed: {
    callId() {
      return this.$route.query.call_id || "";
    },
    ctx() {
      const q = this.$route.query;
      return {
        number: q.number || "",
        agent: q.agent || "",
        date: q.date || "",
        recordingfile: q.recordingfile || "",
      };
    },
    hasAnalysis() {
      return !!this.analysis && Object.keys(this.analysis).length > 0;
    },
    analysisStatus() {
      return this.analysis?.status || "";
    },
    statusBadgeClass() {
      const s = String(this.analysisStatus).toLowerCase();
      if (s === "completed") return "bg-green-100 text-green-800";
      if (s === "failed" || s === "error") return "bg-red-100 text-red-800";
      if (s === "processing" || s === "pending") return "bg-amber-100 text-amber-800";
      return "bg-gray-100 text-gray-600";
    },
    qa() {
      return this.analysis?.qa || null;
    },
    qaChecks() {
      const checks = this.qa?.check_results;
      if (!Array.isArray(checks)) return [];
      // The API returns consecutive duplicate entries; collapse them.
      return this.dedupeConsecutive(
        checks,
        (c) => `${c.check_name}|${c.passed}|${c.reasoning}`
      );
    },
    qaScoreColor() {
      const s = this.qa?.score;
      if (s == null) return "text-gray-400";
      if (s >= 80) return "text-green-600";
      if (s >= 50) return "text-amber-600";
      return "text-red-600";
    },
    qaScoreBarColor() {
      const s = this.qa?.score || 0;
      if (s >= 80) return "bg-green-500";
      if (s >= 50) return "bg-amber-500";
      return "bg-red-500";
    },
    sentiment() {
      return this.analysis?.sentiment || null;
    },
    transcriptLines() {
      const t = this.analysis?.transcript;
      if (!Array.isArray(t)) return [];
      const deduped = this.dedupeConsecutive(
        t,
        (l) => `${l.speaker}|${l.text}`
      );
      return deduped
        .map((l) => ({
          speaker: String(l.speaker || "").toLowerCase() === "agent" ? "agent" : "customer",
          text: l.text || l.content || "",
        }))
        .filter((l) => l.text);
    },
    summaryText() {
      return (
        this.analysis?.summary ||
        this.analysis?.call_summary ||
        this.analysis?.qa?.summary ||
        ""
      );
    },
    summaryHtml() {
      return this.summaryText ? marked.parse(String(this.summaryText)) : "";
    },
  },
  methods: {
    ...mapActions(["GetCallAnalysisDetails", "GetRecordingFile", "SetMessageDialog"]),

    goBack() {
      this.$router.back();
    },

    dedupeConsecutive(arr, keyFn) {
      const out = [];
      let prevKey = null;
      for (const item of arr) {
        const key = keyFn(item);
        if (key !== prevKey) out.push(item);
        prevKey = key;
      }
      return out;
    },

    containsArabic(text) {
      return /[؀-ۿ]/.test(String(text || ""));
    },

    // sentiment score is in [-1, 1]
    sentimentLabel(score) {
      const s = Number(score);
      if (!Number.isFinite(s)) return "-";
      if (s >= 0.25) return this.$t("reports.call_analysis.positive");
      if (s <= -0.25) return this.$t("reports.call_analysis.negative");
      return this.$t("reports.call_analysis.neutral");
    },
    sentimentChipClass(score) {
      const s = Number(score);
      if (!Number.isFinite(s)) return "bg-gray-100 text-gray-600";
      if (s >= 0.25) return "bg-green-100 text-green-800";
      if (s <= -0.25) return "bg-red-100 text-red-800";
      return "bg-amber-100 text-amber-800";
    },
    scoreToPercent(score) {
      const s = Number(score);
      if (!Number.isFinite(s)) return 50;
      // map [-1, 1] -> [0, 100]
      return Math.min(100, Math.max(0, ((s + 1) / 2) * 100));
    },
    formatSigned(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "-";
      return (n > 0 ? "+" : "") + n.toFixed(2);
    },
    formatPercent(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "-";
      const pct = n <= 1 ? n * 100 : n;
      return Math.round(pct) + "%";
    },
    formatCheckType(type) {
      return String(type || "").replace(/_/g, " ");
    },
    async fetchAnalysis() {
      if (!this.callId) {
        this.error = this.$t("reports.call_analysis.not_found");
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const resp = await this.GetCallAnalysisDetails({ call_id: this.callId });
        if (resp && resp.success) {
          const result = resp.result || {};
          // details live under result.analysis; tolerate result.data / result itself
          this.analysis = result.analysis || result.data || result || null;
        } else {
          this.error = resp?.message || this.$t("reports.call_analysis.not_found");
        }
      } catch (err) {
        this.error =
          err.response?.data?.message || err.message || "Failed to load analysis";
      } finally {
        this.loading = false;
      }
    },

    loadRecording() {
      if (!this.ctx.recordingfile) return;
      this.recordingLoading = true;
      this.GetRecordingFile({
        recordingfile: this.ctx.recordingfile,
        callid: this.callId,
        time: this.ctx.date,
        download: false,
      })
        .then((resp) => {
          if (resp.success) {
            this.recordingUrl = URL.createObjectURL(resp.recording);
          } else {
            this.SetMessageDialog({ show: true, message: resp.message });
          }
        })
        .catch((err) => {
          const message = err.response ? err.response.data.message : err.message;
          this.SetMessageDialog({ show: true, message });
        })
        .finally(() => {
          this.recordingLoading = false;
        });
    },
  },
  mounted() {
    this.fetchAnalysis();
  },
};
</script>

<style scoped></style>
